import React from 'react';
import cn from 'classnames';
import { Check, X } from 'react-feather';
import QuestionDetails from 'components/shared/QuestionDetails';
import OpenDownload from 'components/Questions/UploadResponseQuestion/OpenDownload';
import VideoResponse from 'components/VideoResponse/Grading/VideoResponse';
import ConditionalList from 'components/shared/ConditionalList';
import CriteriaList from 'components/VideoResponse/Grading/CriteriaList';
import PreserveFormatting from 'components/shared/PreserveFormatting';
import ChatDetails from './Chat/ChatDetails';

const ReportCardDesktop = ({
  hasGraderFeedback,
  canViewRecordingLink,
  api,
  currentUserId,
  ratings,
  currentUserName,
  progressUserId,
  answers,
  isAdmin,
}) => {
  const renderAnswers = (answer, initialStateVideoResponse) => {
    if (answer.isMediaResponse && answer.isAnswerPresent) {
      return <VideoResponse initialState={initialStateVideoResponse} />;
    }
    if (answer.hasUpload) {
      return (
        <OpenDownload
          answerId={answer.answer.id}
          fileName={answer.answer.given_answer}
          finished
        />
      );
    }
    if (answer.hasAnswerWithMetadata) {
      return <QuestionDetails details={answer.answer.given_answer} />;
    }
    return <ConditionalList list={answer.givenAnswerList} />;
  };

  const renderQuestions = answer => {
    if (answer.hasMetadata && !answer.emptyMetadata) {
      return (
        <>
          <div>{answer.questionText}</div>
          <QuestionDetails
            details={answer.details}
            questionType={answer.questionType}
          />
        </>
      );
    }
    if (answer.isChat && !answer.emptyMetadata) {
      return (
        <>
          <div>{answer.questionText}</div>
          <ChatDetails details={answer.details} />
        </>
      );
    }
    if (answer.isDeleted) {
      return (
        <small className="ReportCard-question--deleted">
          {t('questions.question.deleted')}{' '}
        </small>
      );
    }
    return <div>{answer.questionText}</div>;
  };

  const correctnessIconFragment = answer => {
    const icon = answer.answer.correct ? (
      <Check strokeWidth="6" size="18" className="c-success pr-4 pt-6" />
    ) : (
      <X strokeWidth="6" size="18" className="c-danger-red pr-4 pt-6" />
    );
    return answer.answerIsCorrectOrIncorrect ? icon : false;
  };

  const RenderRows = () => {
    return answers.map(answer => {
      const data = {
        id: answer.id,
        criteriaRatings: answer.criteriaRatings,
        ratingOptions: ratings,
        preview: true,
        i18n: {
          no_description: t('feedback_criteria.criterion.no_description'),
        },
      };
      const initialStateVideoResponse = {
        data: {
          questionAutomatedAnalysis: answer.answer.question_automated_analysis,
          questionAutomatedAnalysisKeywordMatchingEnabled:
            answer.answer.question_automated_analysis_keyword_matching_enabled,
          questionAutomatedAnalysisWordsPerMinuteEnabled:
            answer.answer
              .question_automated_analysis_words_per_minute_enabled || false,
          questionAutomatedAnalysisFillerWordsEnabled:
            answer.answer.question_automated_analysis_filler_words_enabled ||
            false,
          questionAutomatedAnalysisConfidenceEnabled:
            answer.answer.question_automated_analysis_confidence_enabled ||
            false,
          id: answer.answer.id,
          questionId: answer.answer.question_id,
          currentUser: currentUserName,
          gradable: answer.answer.gradable,
          question: answer.answer.question_text,
          questionType: answer.answer.question_type,
          correctAnswer: answer.answer.correct,
          feedback: answer.answer.feedback,
          givenAnswer: answer.answer.given_answer,
          givenAnswerIsIlos: answer.givenAnswerIsIlos,
          preview: true,
          isCurrentUser: currentUserId === progressUserId,
          currentUserId,
          isAdmin,
          criteriaRatings: answer.criteriaRatings,
          ratingOptions: ratings,
          mediaShareLink: answer.mediaShareLink,
          recording: answer.serializedRecording,
          skipReason: answer.skipReason,
          metadata: answer.answer.metadata || {},
        },
        meta: {
          skipped: answer.skipped,
          feedbackCriteriaPresent: answer.feedbackCriteriaPresent,
          apiKey: api,
          canViewSharableRecordingLink: canViewRecordingLink,
        },
        i18n: {
          questions: t('questions'),
          common: t('common'),
          progress: t('progress'),
          grades: t('grades'),
          no_description: t('feedback_criteria.criterion.no_description'),
        },
      };

      const rowClassNames = `ReportCard-row ${answer.rowStatusClass || ''}`;
      return (
        <>
          <tr className={rowClassNames}>
            <td className="ReportCard-rowNumber ReportCard-rowNumber--counter">
              {correctnessIconFragment(answer)}
            </td>
            <td className="ReportCard-divider" />
            <td
              className={cn({
                'ReportCard-question': !answer.isChat,
                'ReportCard-chat': answer.isChat,
              })}
            >
              {renderQuestions(answer)}
            </td>
            <td className={answer.givenAnswerClass}>
              {renderAnswers(answer, initialStateVideoResponse)}
            </td>

            <td className={answer.correctAnswerClass}>
              <ConditionalList list={answer.correctAnswerList} />{' '}
            </td>
            {hasGraderFeedback && (
              <td className="ReportCard-feedback">
                {answer.feedbackCriteriaPresent && <CriteriaList data={data} />}
                {answer.feedbackCriteriaPresent && (
                  <div className="fw-b">{t('grades.feedback.comments')}:</div>
                )}
                <div>
                  {(() => {
                    if (answer.questionType === 'multiple_choice')
                      return (
                        <p>
                          <em>{t('grades.feedback.not_available')}</em>
                        </p>
                      );
                    if (answer.answer.feedback && answer.answer.feedback.length)
                      return (
                        <PreserveFormatting>
                          {answer.answer.feedback}
                        </PreserveFormatting>
                      );
                    return (
                      <p>
                        <em>{t('grades.feedback.none_provided')}</em>
                      </p>
                    );
                  })()}
                </div>
              </td>
            )}
            <td>{answer.graderName || t('progress.report_card.no_grader')}</td>
          </tr>
        </>
      );
    });
  };

  return (
    <>
      <table className="ReportCard scored">
        <thead>
          <tr>
            <th className="ReportCard-rowNumber" />
            <th className="ReportCard-divider" />
            <th className="ReportCard-question">
              {t('progress.report_card.question')}
            </th>
            <th className="ReportCard-givenAnswer">
              {t('progress.report_card.given_answer')}
            </th>
            <th className="ReportCard-correctAnswer">
              {t('progress.report_card.correct_answer')}
            </th>
            {hasGraderFeedback && <th>{t('progress.report_card.feedback')}</th>}
            <th>{t('progress.report_card.graded_by')}</th>
          </tr>
        </thead>
        <tbody>
          <RenderRows />
        </tbody>
      </table>
    </>
  );
};

export default ReportCardDesktop;
